import {
  customer_category_trees_create_post,
  customer_category_trees_import_post,
  customer_category_trees_import_template_get,
  customer_category_trees_nodes_nodeId_delete_post,
  customer_category_trees_nodes_nodeId_move_post,
  customer_category_trees_nodes_nodeId_update_post,
  customer_category_trees_page_post,
  customer_category_trees_treeId_delete_post,
  customer_category_trees_treeId_copy_post,
  customer_category_trees_treeId_export_get,
  customer_category_trees_treeId_get,
  customer_category_trees_treeId_nodes_create_post,
  customer_category_trees_treeId_update_post
} from '@/server/modules/25820'

const genKey = prefix =>
  `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`

const unwrap = response => {
  if (response?.success === false) {
    throw new Error(response.retInfo || '接口请求失败')
  }
  return response?.data
}

const i18nValue = (i18nNames, keys) => {
  const source = i18nNames || {}
  for (const key of keys) {
    if (source[key]) return source[key]
  }
  return ''
}

export const normalizeCustomerCategoryNode = node => {
  const nodeId = node.nodeId ?? node.id ?? node.key
  const defaultName =
    node.defaultName ?? node.nameCn ?? node.name ?? node.title ?? ''
  const i18nNames = node.i18nNames || node.localizedNames || {}
  const englishName =
    i18nValue(i18nNames, ['en_US', 'en-US', 'en']) ||
    node.nameEn ||
    node.localeNameUs ||
    ''
  const children = node.children || node.childNodes || node.nodes || []

  return {
    ...node,
    id: nodeId,
    nodeId,
    key: nodeId || genKey('node'),
    defaultName,
    title: defaultName,
    nameCn: defaultName,
    nameEn: englishName,
    localeNameUs: englishName,
    i18nNames,
    status: Number(node.status) === 0 ? 0 : 1,
    icon: node.icon || '',
    children: children.map(normalizeCustomerCategoryNode)
  }
}

const normalizeTreeData = treeData =>
  (treeData || []).map(normalizeCustomerCategoryNode)

const levelCounts = nodes => {
  const counts = [0, 0, 0]
  const visit = (items, level = 0) => {
    ;(items || []).forEach(item => {
      if (level < counts.length) counts[level]++
      visit(item.children, level + 1)
    })
  }
  visit(nodes)
  return counts
}

export const normalizeCustomerCategoryTree = item => {
  const treeData = normalizeTreeData(
    item.treeData || item.nodes || item.categoryNodes || []
  )
  const rawLevelCounts = item.levelNodeCounts || []
  const counts = rawLevelCounts.length
    ? [1, 2, 3].map(level => {
        const current = rawLevelCounts.find(
          count => Number(count.level) === level
        )
        return Number(current?.nodeCount || 0)
      })
    : levelCounts(treeData)
  const mappingNames =
    item.mappingNames ||
    item.referencedMappingNames ||
    item.mappingNameList ||
    item.mappings ||
    []
  const mappingTags = (Array.isArray(mappingNames)
    ? mappingNames
    : mappingNames
    ? [mappingNames]
    : []
  )
    .map(mapping =>
      typeof mapping === 'object'
        ? mapping.mappingName || mapping.name || mapping.label
        : mapping
    )
    .filter(Boolean)
  const mappingLabel =
    item.mappingLabel || item.mappingName || mappingTags.join('、') || ''
  if (!mappingTags.length && mappingLabel) mappingTags.push(mappingLabel)

  return {
    ...item,
    id: item.treeId ?? item.id,
    treeId: item.treeId ?? item.id,
    treeName: item.treeName || item.name || '',
    description: item.description || '',
    mappingLabel,
    mappingTags,
    referenced:
      item.referenced === true ||
      item.isReferenced === true ||
      Boolean(mappingLabel),
    nodeCount:
      item.nodeCount ??
      item.totalNodeCount ??
      counts.reduce((total, count) => total + count, 0),
    levelCounts: item.levelCounts || counts,
    updateTime:
      item.lastUpdateTime ||
      item.updateTime ||
      item.updatedAt ||
      item.modifyTime ||
      '-',
    treeData
  }
}

const normalizePage = data => {
  const source = data || {}
  const list = source.list || source.records || source.rows || []
  return {
    list: list.map(normalizeCustomerCategoryTree),
    total: Number(source.total ?? source.totalCount ?? list.length)
  }
}

const referencedValue = status => {
  if (status === '1' || status === 'mapped' || status === true) return true
  if (status === '2' || status === 'unmapped' || status === false) return false
  return undefined
}

export const listCustomerCategoryTrees = async params => {
  const payload = {
    ...params,
    referenced: referencedValue(params.status)
  }
  delete payload.status
  const data = unwrap(await customer_category_trees_page_post(payload))
  return normalizePage(data)
}

export const getCustomerCategoryTree = async id => {
  const data = unwrap(await customer_category_trees_treeId_get({ treeId: id }))
  return data ? normalizeCustomerCategoryTree(data) : null
}

export const createCustomerCategoryTree = async payload => {
  const data = unwrap(
    await customer_category_trees_create_post({
      brandId: payload.brandId,
      siteId: payload.siteId,
      treeName: payload.treeName,
      description: payload.description
    })
  )
  return {
    id: data?.treeId ?? data?.id ?? data,
    treeId: data?.treeId ?? data?.id ?? data
  }
}

export const copyCustomerCategoryTree = async (id, treeName) => {
  const data = unwrap(
    await customer_category_trees_treeId_copy_post({ treeId: id, treeName })
  )
  const treeId = data?.treeId ?? data?.id ?? data
  return { id: treeId, treeId }
}

export const updateCustomerCategoryTree = async (id, payload) => {
  const data = unwrap(
    await customer_category_trees_treeId_update_post({
      treeId: id,
      treeName: payload.treeName,
      description: payload.description
    })
  )
  return data
}

export const deleteCustomerCategoryTree = async id => {
  return unwrap(
    await customer_category_trees_treeId_delete_post({ treeId: id })
  )
}

const normalizeI18nNames = node => {
  const result = {}
  Object.entries(node.i18nNames || {}).forEach(([langCode, name]) => {
    if (!name) return
    const normalizedCode = `${langCode}`.trim().replace(/_/g, '-')
    if (normalizedCode) result[normalizedCode] = name
  })
  return result
}

const nodePayload = node => ({
  defaultName: node.defaultName ?? node.nameCn ?? node.title,
  i18nNames: normalizeI18nNames(node),
  icon: node.icon || '',
  status: Number(node.status) === 0 ? 0 : 1
})

export const createCustomerCategoryNode = async (treeId, node, parentId) => {
  const payload = nodePayload(node)
  const data = unwrap(
    await customer_category_trees_treeId_nodes_create_post({
      treeId,
      parentId,
      defaultName: payload.defaultName,
      i18nNames: payload.i18nNames,
      icon: payload.icon
    })
  )
  return data?.nodeId ?? data?.id ?? data
}

export const updateCustomerCategoryNode = async node => {
  return unwrap(
    await customer_category_trees_nodes_nodeId_update_post({
      nodeId: node.nodeId || node.id || node.key,
      ...nodePayload(node)
    })
  )
}

export const deleteCustomerCategoryNode = async nodeId => {
  return unwrap(
    await customer_category_trees_nodes_nodeId_delete_post({ nodeId })
  )
}

export const moveCustomerCategoryNode = async (
  nodeId,
  targetParentId,
  orderedNodeIds
) => {
  return unwrap(
    await customer_category_trees_nodes_nodeId_move_post({
      nodeId,
      targetParentId,
      root: !targetParentId,
      orderedNodeIds
    })
  )
}

const downloadBlobFile = (filename, content) => {
  const blob = content instanceof Blob ? content : new Blob([content])
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0)
}

export const downloadTextFile = (
  filename,
  content,
  mimeType = 'application/json'
) => {
  downloadBlobFile(
    filename,
    new Blob([content], { type: `${mimeType};charset=utf-8` })
  )
}

const ensureDownloadFile = async response => {
  if (!(response instanceof Blob)) return response
  if (!response.type?.includes('json')) return response
  const result = JSON.parse(await response.text())
  if (result?.success === false) {
    throw new Error(result.retInfo || '文件下载失败')
  }
  return result.data
}

export const downloadCustomerCategoryTreeImportTemplate = async context => {
  const response = await customer_category_trees_import_template_get(
    {
      brandId: context.brandId,
      siteId: context.siteId
    },
    { responseType: 'blob' }
  )
  const file = await ensureDownloadFile(response)
  downloadBlobFile('对客类目树导入模板.xlsx', file)
}

export const exportCustomerCategoryTree = async id => {
  return unwrap(await customer_category_trees_treeId_export_get({ treeId: id }))
}

export const importCustomerCategoryTree = async payload => {
  const response = await customer_category_trees_import_post({
    brandId: payload.brandId,
    siteId: payload.siteId,
    treeName: payload.treeName,
    description: payload.description || '',
    file: payload.file
  })
  if (Array.isArray(response?.data?.errors) && response.data.errors.length) {
    return response.data
  }
  return unwrap(response)
}
